import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { useAuth } from "@/App";
import { Users, UserPlus, Mail, Shield, ShieldCheck, Crown, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamPage() {
  const { isAdmin, isOwner, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const [membersRes, invitationsRes] = await Promise.all([
        fetch(`${API}/team`, { credentials: "include" }),
        isAdmin ? fetch(`${API}/team/invitations`, { credentials: "include" }) : Promise.resolve({ ok: true, json: () => [] }),
      ]);

      if (membersRes.ok) {
        setMembers(await membersRes.json());
      }
      if (invitationsRes.ok) {
        setInvitations(await invitationsRes.json());
      }
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (email, role) => {
    try {
      const response = await fetch(`${API}/team/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, role }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      const result = await response.json();
      toast.success(result.message);
      setInviteModalOpen(false);
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      const response = await fetch(`${API}/team/${memberId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Membre supprimé");
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const response = await fetch(`${API}/team/${memberId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Rôle mis à jour");
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${API}/team/invitations/${invitationId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Erreur");

      toast.success("Invitation annulée");
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "owner":
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Crown className="w-3 h-3 mr-1" />Propriétaire</Badge>;
      case "admin":
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200"><ShieldCheck className="w-3 h-3 mr-1" />Admin</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200"><Shield className="w-3 h-3 mr-1" />Membre</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Équipe" />
      
      <main className="p-4 space-y-4 max-w-4xl mx-auto">
        {/* Header Actions */}
        {isAdmin && (
          <Button
            onClick={() => setInviteModalOpen(true)}
            className="w-full h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-2xl"
            data-testid="invite-member-btn"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Inviter un membre
          </Button>
        )}

        {/* Team Members */}
        <Card className="card-festive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-heading">
              <Users className="w-5 h-5 text-festive-pink" />
              Membres ({members.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors"
                data-testid={`member-${member.user_id}`}
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={member.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-festive-pink to-festive-purple text-white">
                    {member.name?.charAt(0)?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{member.name}</p>
                    {member.user_id === user?.user_id && (
                      <Badge variant="outline" className="text-xs">Vous</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{member.email}</p>
                </div>

                <div className="flex items-center gap-2">
                  {isAdmin && member.role !== "owner" && member.user_id !== user?.user_id ? (
                    <Select
                      value={member.role}
                      onValueChange={(value) => handleUpdateRole(member.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="member">Membre</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    getRoleBadge(member.role)
                  )}

                  {isAdmin && member.role !== "owner" && member.user_id !== user?.user_id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce membre ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {member.name} n'aura plus accès à cette organisation.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleRemoveMember(member.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        {isAdmin && invitations.length > 0 && (
          <Card className="card-festive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Clock className="w-5 h-5 text-amber-500" />
                Invitations en attente ({invitations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-amber-50 border border-amber-200"
                >
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-600" />
                  </div>
                  
                  <div className="flex-1">
                    <p className="font-medium">{invitation.email}</p>
                    <p className="text-sm text-gray-500">
                      Rôle: {invitation.role === "admin" ? "Admin" : "Membre"}
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    Annuler
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="card-festive bg-gradient-to-br from-festive-pink/10 to-festive-purple/10">
          <CardContent className="p-4">
            <h4 className="font-semibold mb-2">Rôles disponibles</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" />
                <span><strong>Propriétaire</strong> - Accès total, gestion de l'équipe</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-purple-500" />
                <span><strong>Admin</strong> - Gestion catalogue, équipe, réservations</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span><strong>Membre</strong> - Consultation et gestion des réservations</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Invite Modal */}
      <InviteModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onInvite={handleInvite}
      />

      <BottomNav />
    </div>
  );
}

const InviteModal = ({ open, onClose, onInvite }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.error("Email requis");
      return;
    }
    setLoading(true);
    await onInvite(email, role);
    setLoading(false);
    setEmail("");
    setRole("member");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Inviter un membre</DialogTitle>
          <DialogDescription>
            Envoyez une invitation pour rejoindre votre organisation
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-festive mt-1"
              placeholder="collaborateur@example.com"
            />
          </div>
          
          <div>
            <Label>Rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="input-festive mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin - Accès complet</SelectItem>
                <SelectItem value="member">Membre - Gestion réservations</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !email.trim()}
            className="bg-gradient-to-r from-festive-pink to-festive-purple text-white"
          >
            {loading ? "Envoi..." : "Envoyer l'invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
